import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import StoreUtils from 'src/app/public/store/utils/StoreUtils';
import { GoogleMapsApiKeyService } from 'src/app/google-maps-api-key.service';
import {
  CoordinateInput,
  hasPinLocation,
  MapPinLocation,
} from './map-pin.helper';

declare var google: any;

/** Web Mercator cannot project past roughly ±85.05°, so a random latitude is
 *  drawn from that band rather than the full ±90 — outside it the Static Maps
 *  API clamps and every draw returns the same polar strip. */
const MAX_PLACEHOLDER_LATITUDE = 85;

/** The placeholder is centred on a meaningless point, so it is drawn at country
 *  scale rather than the street-level `zoom` a real address gets. Most of the
 *  globe is open water: at street level the Static Maps API returns a featureless
 *  blue square, which reads as a broken image rather than as "no pin yet". This
 *  zoom is also what the live map opens on, so the admin can pan to their city
 *  instead of starting stranded mid-ocean. */
const PLACEHOLDER_ZOOM = 3;

@Component({
  selector: 'app-map-pin-widget',
  templateUrl: './map-pin-widget.component.html',
  styleUrls: ['./map-pin-widget.component.scss'],
})
export class MapPinWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('mapWrapper') mapElement: ElementRef;

  @Input() latitude: CoordinateInput = null;
  @Input() longitude: CoordinateInput = null;
  @Input() draggable: boolean = false;
  @Input() zoom: number = 13;
  @Input() height: string = '300px';
  @Input() width: string = '100%';

  @Output() locationChanged = new EventEmitter<MapPinLocation>();
  @Output() mapInitialized = new EventEmitter<void>();

  map: google.maps.Map;
  marker: google.maps.Marker;

  // Default to a lightweight Static Maps image; only build the interactive
  // (billed) google.maps.Map when the admin taps the pencil to edit (#10476).
  isEditing = false;
  staticMapUrl: string = null;

  /** The last position this widget emitted, so the host echoing it back through
   *  the @Input bindings is not mistaken for a new location to pan to. */
  private lastEmitted: MapPinLocation = null;
  /** Drawn once per widget instance and kept for its lifetime. Recomputing it
   *  per change-detection pass would hand the <img> a new src every pass, which
   *  refetches (and rebills) a Static Maps image on every keystroke. */
  private placeholder: MapPinLocation = null;
  private destroyed = false;
  private mapSetupTimeout: any;
  private mapInitializedTimeout: any;
  private mapInitAttempts = 0;

  constructor(private apiKeyService: GoogleMapsApiKeyService) {}

  /** True when the record itself carries a position, as opposed to the widget
   *  showing a placeholder on its behalf. */
  get hasOwnLocation(): boolean {
    return hasPinLocation(this.latitude, this.longitude);
  }

  /** True when the record has no position of its own, so the map on screen is
   *  the random placeholder and means nothing. The widget deliberately has no
   *  second real position to fall back on: centring an empty record on some
   *  other record's coordinates — the parent store's, say — renders a plausible
   *  address that the admin never entered and that will not be saved (#11028). */
  get isShowingPlaceholder(): boolean {
    return !this.hasOwnLocation;
  }

  /** A random point on Earth, so a record with no address still renders a map
   *  and a pencil to edit it rather than an empty gap (#11028). Display-only:
   *  it is never emitted and never written to the host's form, so a record the
   *  admin never touched still saves no coordinates. */
  private get placeholderLocation(): MapPinLocation {
    if (!this.placeholder) {
      const round = (value: number) => Math.round(value * 1e6) / 1e6;
      const latitudeSpan = 2 * MAX_PLACEHOLDER_LATITUDE;
      this.placeholder = {
        lat: round(Math.random() * latitudeSpan - MAX_PLACEHOLDER_LATITUDE),
        lng: round(Math.random() * 360 - 180),
      };
    }
    return this.placeholder;
  }

  /** The position the widget DISPLAYS: the record's own coordinates, or a random
   *  placeholder when it has none. Only the first is real — dragging the pin
   *  emits the dragged position, so the placeholder never reaches the form. */
  private get resolvedLocation(): MapPinLocation {
    if (this.hasOwnLocation) {
      return { lat: Number(this.latitude), lng: Number(this.longitude) };
    }
    return this.placeholderLocation;
  }

  /** Street level for a real address, country scale for the placeholder. Shared
   *  by the static image and the live map so the pencil does not change what the
   *  admin was looking at. */
  private get displayZoom(): number {
    return this.isShowingPlaceholder ? PLACEHOLDER_ZOOM : this.zoom;
  }

  /** The widget always has something to show, so the only thing that hides it is
   *  the Google Maps script being unavailable — staticMapUrl is never set then
   *  and startEditing() refuses to run. */
  get isVisible(): boolean {
    return this.isEditing || !!this.staticMapUrl;
  }

  ngOnInit(): void {
    // Hosts all bind the coordinates, so ngOnChanges has normally painted the
    // image by now; this only covers a host that binds none of them.
    if (!this.staticMapUrl) {
      this.showStaticMap();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only the coordinates move the map. Redrawing on any other input would hand
    // the <img> a new src on inputs that cannot change where the pin is, and
    // every new src refetches a billed Static Maps image.
    if (!changes['latitude'] && !changes['longitude']) {
      return;
    }
    if (this.isEditing) {
      // While the live map is being built the marker does not exist yet; the
      // build reads the current inputs, so there is nothing to do here.
      this.syncMarkerToInputs();
      return;
    }
    this.showStaticMap();
  }

  showStaticMap(): void {
    if (StoreUtils.isMapsDisabled()) {
      this.staticMapUrl = null;
      return;
    }
    const location = this.resolvedLocation;
    this.staticMapUrl = this.apiKeyService.getStaticMapUrl(
      location.lat,
      location.lng,
      {
        zoom: this.displayZoom,
        // No pin on the placeholder: the record has no position, and a marker
        // would assert one at a point picked at random.
        marker: !this.isShowingPlaceholder,
      },
    );
  }

  startEditing(): void {
    // Guard re-entry: a map load is billed per instantiation, so a double-click
    // on the pencil must not build two of them.
    if (this.isEditing || StoreUtils.isMapsDisabled()) {
      return;
    }
    // Stays live for the rest of the session — toggling back to the static
    // image and in again would be billed twice.
    this.isEditing = true;
    this.initializeMap();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.mapSetupTimeout);
    clearTimeout(this.mapInitializedTimeout);
  }

  initializeMap(): void {
    this.mapInitAttempts = 0;
    this.scheduleMapCreate(0);
  }

  /** No delay in the common case: no host runs OnPush, so #mapWrapper is in the
   *  DOM by the end of the change-detection pass that set isEditing. The bounded
   *  retry covers the rare case where it is not. This used to be two chained
   *  500ms timers, which left an empty bordered box on screen for a second —
   *  tolerable behind a pencil click, but not on every empty address form now
   *  that this state opens the map by itself. */
  private scheduleMapCreate(delay: number): void {
    this.mapSetupTimeout = setTimeout(() => {
      if (this.destroyed || StoreUtils.isMapsDisabled()) {
        return;
      }
      if (!this.mapElement || !this.mapElement.nativeElement) {
        if (this.mapInitAttempts++ >= 20) {
          return;
        }
        this.scheduleMapCreate(50);
        return;
      }
      this.createMap();
    }, delay);
  }

  private createMap(): void {
    // Read the coordinates here rather than when editing started, so a value
    // that arrived during the setup delay is the one the map opens on.
    const location = this.resolvedLocation;
    const center = new google.maps.LatLng(location.lat, location.lng);

    const mapOptions: google.maps.MapOptions = {
      zoom: this.displayZoom,
      center,
      scrollwheel: false,
      draggable: true,
    };
    this.map = new google.maps.Map(this.mapElement.nativeElement, mapOptions);

    if (this.marker) {
      this.marker.setMap(null);
    }

    this.marker = new google.maps.Marker({
      position: center,
      map: this.map,
      draggable: this.draggable,
      title: this.draggable ? 'Drag me!' : 'Location',
    });

    if (this.draggable) {
      this.marker.addListener('dragend', (event) => {
        const position = event.latLng;
        this.lastEmitted = { lat: position.lat(), lng: position.lng() };
        this.locationChanged.emit(this.lastEmitted);
      });
    }

    this.mapInitializedTimeout = setTimeout(() => {
      this.mapInitialized.emit();
    }, 1000);
  }

  private syncMarkerToInputs(): void {
    if (!this.map || !this.marker) {
      return;
    }
    const { lat, lng } = this.resolvedLocation;
    // The host writes a dragged pin back into the form, which comes straight
    // back here as an input change. Re-centring on it would yank the map so the
    // pin the admin just dropped jumps to the middle of the viewport.
    if (
      this.lastEmitted &&
      this.lastEmitted.lat === lat &&
      this.lastEmitted.lng === lng
    ) {
      return;
    }
    const position = new google.maps.LatLng(lat, lng);
    this.marker.setPosition(position);
    this.map.setCenter(position);
  }
}
